import {
  ConnectedAccountFullSchema,
  ConnectedAccountGroups,
  ConnectedAccountListSchema,
  ConnectedAccounts,
} from 'pejer/src/tables/ConnectedAccounts'
import { CustomerFullSchema, CustomerGroups, CustomerListSchema } from 'pejer/src/tables/Customer'
import { PaymentFullSchema, PaymentGroups, PaymentListSchema } from 'pejer/src/tables/Payment'
import { PayoutFullSchema, PayoutGroups, PayoutListSchema } from 'pejer/src/tables/Payout'
import { createFormatter } from '../helpers'
import { BusinessFullSchema, BusinessGroups, BusinessListSchema } from '../tables/Businesses'
import {
  KidNorwayTransactionFullSchema,
  KidNorwayTransactionGroups,
  KidNorwayTransactionListSchema,
} from '../tables/KidNorwayTransaction'
import {
  PersonsOfInterestFullSchema,
  PersonsOfInterestGroups,
  PersonsOfInterestListSchema,
} from '../tables/PersonsOfInterest'
import { RefundGroups, RefundsListSchema } from '../tables/Refunds'
import { UserFullSchema, UserGroups } from '../tables/Users'
import { ApiUrls, ConfigUrls, PejerTablesFull, TableConfig } from '../types/base'
import { PaymentVoucherDto } from '../types/PaymentVoucher'
import { PlatformUserRoles } from '../types/Roles'
import { TableStats } from '../types/stats'
import { SignedInUser } from '../types/types'
import { businessColumns } from './columns/Businesses'
import { connectedAccountsColumns } from './columns/ConnectedAccounts'
import { customersColumns } from './columns/Customers'
import { kidNorwayTransactionsColumns } from './columns/KidNorwayTransactions'
import { paymentsColumns } from './columns/Payments'
import { payoutsColumns } from './columns/Payouts'
import personsOfInterestColumns from './columns/PersonsOfInterest'
import { refundsColumns } from './columns/Refunds'
import { usersColumns } from './columns/Users'

export const defaultTables = {
  payments: { list: PaymentListSchema, full: PaymentFullSchema, groups: PaymentGroups },
  customers: { list: CustomerListSchema, full: CustomerFullSchema, groups: CustomerGroups },
  payouts: { list: PayoutListSchema, full: PayoutFullSchema, groups: PayoutGroups },
  connectedAccounts: {
    list: ConnectedAccountListSchema,
    full: ConnectedAccountFullSchema,
    groups: ConnectedAccountGroups,
  },
  users: { list: UserFullSchema, full: UserFullSchema, groups: UserGroups },
  businesses: {
    list: BusinessListSchema,
    full: BusinessFullSchema,
    groups: BusinessGroups,
  },
  personsOfInterest: {
    list: PersonsOfInterestListSchema,
    full: PersonsOfInterestFullSchema,
    groups: PersonsOfInterestGroups,
  },
  kidNorwayTransactions: {
    list: KidNorwayTransactionListSchema,
    full: KidNorwayTransactionFullSchema,
    groups: KidNorwayTransactionGroups,
  },
  refunds: {
    list: RefundsListSchema,
    full: RefundsListSchema,
    groups: RefundGroups,
  },
} as const satisfies TableConfig

export const defaultColumns = {
  payments: paymentsColumns,
  customers: customersColumns,
  payouts: payoutsColumns,
  connectedAccounts: connectedAccountsColumns,
  users: usersColumns,
  businesses: businessColumns,
  personsOfInterest: personsOfInterestColumns,
  kidNorwayTransactions: kidNorwayTransactionsColumns,
  refunds: refundsColumns,
}

export type TypeFromFormatter<T extends ApiUrls<L>, L = unknown> = Parameters<T['formatter']>[0]

export const defaultUrls = {
  routes: {
    // pass in email and password to get a token
    signIn: {
      path: '/api/signin/authenticate',
      method: 'POST',
      formatter: createFormatter<string>(),
    },

    // gets the user from the token
    validateJWT: {
      path: '/api/signin/ValidateJwt',
      method: 'POST',
      formatter: createFormatter<SignedInUser>(),
    },

    // setup One-Time Password
    twoFactorSetup: {
      path: '/api/signin/TwoFactor/setup',
      method: 'GET',
      formatter: createFormatter<{ secretKey: string; qrCodeUrl: string; qrCodeImage: string }>(),
    },

    // validate One-Time Password
    twoFactorValidate: {
      path: '/api/signin/TwoFactor/validate',
      method: 'POST',
      formatter: createFormatter<string>(),
    },
  },
  connectedAccounts: {
    list: {
      path: '/api/account/list',
      method: 'GET',
      formatter: createFormatter<ConnectedAccounts.ListItem[]>(),
    },
    get: {
      path: '/api/account/{id}',
      method: 'GET',
      formatter: createFormatter<ConnectedAccounts.Full>(),
    },
    stats: {
      path: '/api/account/stats',
      method: 'GET',
      formatter: createFormatter<
        TableStats<{
          payoutsEnabledCount: number
          payoutsDisabledCount: number
        }>[]
      >(),
    },
    statistics: { path: '/api/account/{ConnectedAccountID}/statistics', method: 'GET' },
    statisticsAll: { path: '/api/account/statistics', method: 'GET' },
    update: { path: '/api/account/update', method: 'POST' },
    'rerun-webhook': {
      path: '/api/account/{id}/webhook/rerun',
      method: 'POST',
      permissions: [PlatformUserRoles.CustomerSupport],
    },
  },
  refunds: {
    get: {
      path: '/api/refund/{id}',
      method: 'GET',
    },
    list: {
      path: '/api/refund/list',
      method: 'GET',
    },
  },
  businesses: {
    get: {
      path: '/businesses/{countryCode}/{organizationId}',
      method: 'GET',
    },
    list: {
      path: '/businesses',
      method: 'GET',
    },
    stats: {
      path: '/api/business/stats',
      method: 'GET',
      formatter: createFormatter<
        TableStats<{
          complianceRiskGreenCount: number
          complianceRiskYellowCount: number
          complianceRiskRedCount: number
          onboardedCount: number
          missingSignaturesCount: number
          tosNotAcceptedCount: number
        }>[]
      >(),
    },
  },
  customers: {
    list: { path: '/api/customer/list', method: 'GET' },
    get: { path: '/api/customer/{id}', method: 'GET' },
  },
  payments: {
    list: { path: '/api/payment/list', method: 'GET' },
    get: {
      path: '/api/payment/{id}',
      method: 'GET',
      formatter: createFormatter<PejerTablesFull<'payments'>>(),
    },
    stats: {
      path: '/api/payment/stats',
      method: 'GET',
      formatter: createFormatter<
        TableStats<{
          initiatedPaymentCount: number
          reservedPaymentCount: number
          refundedPaymentCount: number
          chargedPaymentCount: number
          failedPaymentCount: number
          cancelledPaymentCount: number
          totalTransactionVolume: number
        }>[]
      >(),
    },
    updateDebtCollectionProperty: {
      path: '/api/payment/{id}/property/debtCollectionActivated',
      method: 'PATCH',
    },
  },
  kidNorwayTransactions: {
    list: { path: '/kidnorway/OCRRecords', method: 'GET' },
    get: { path: '/kidNorwayTransaction/{id}', method: 'GET' },
  },
  payouts: {
    list: { path: '/api/payout/list', method: 'GET' },
    get: { path: '/api/payout/{id}', method: 'GET' },
    create: {
      path: '/api/payout/create/{connectedAccountID}',
      method: 'POST',
      permissions: [PlatformUserRoles.FinancialAdmin],
    },
    getPaymentVoucher: {
      path: '/api/payouts/{id}/payment-voucher',
      method: 'GET',
      formatter: createFormatter<PaymentVoucherDto>(),
      // permissions: [],
      unauthenticatedAccess: true,
    },
  },
  users: {
    get: { path: '/api/users/{id}', method: 'GET' },
    list: { path: '/api/users', method: 'GET' },
    create: { path: '/api/users', method: 'POST', permissions: [PlatformUserRoles.Admin] },
    delete: { path: '/api/users/{id}', method: 'DELETE', permissions: [PlatformUserRoles.Admin] },
    edit: { path: '/api/users/{id}', method: 'PATCH', permissions: [PlatformUserRoles.Admin] },
  },
  personsOfInterest: {
    list: { path: '/personsOfInterest', method: 'GET' },
    get: { path: '/personsOfInterest/{id}', method: 'GET' },
  },
  webhookChannels: {
    delete: { path: '/api/webhook/channels/{id}/delete', method: 'POST' },
    update: { path: '/api/webhook/channels/{id}/update', method: 'POST' },
    create: { path: '/api/webhook/channels/create', method: 'POST' },
    list: { path: '/api/webhook/channels/list', method: 'GET' },
  },
  webhooks: {
    rerun: { path: '/api/webhook/{id}/rerun', method: 'POST' },
    list: { path: '/api/webhook/list', method: 'GET' },
  },
  apiRequest: { list: { path: '/api/apiRequest/list', method: 'GET' } },
} as const satisfies ConfigUrls

// export const config = {
//   cookieNames: {
//     platformToken: 'x-platform-token',
//     enviroment: 'x-platform-environment',
//   },
//   urls: defaultUrls,
//   columns: defaultColumns,
//   schema: {
//     tables: defaultTables,
//   },
// } as const satisfies Config<typeof defaultUrls, typeof defaultTables>
